import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Business as BusinessIcon, Google, Microsoft } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AuthAPI } from "../api/auth";
import { useAuth } from "../providers/AuthProvider";
import { ROUTES } from "../routes";

const FORM_MAX_WIDTH = 860;
const FIELD_WIDTH = 320;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    gdpr_consent: false,
    referralCode: "",
  });

  const openSnack = (msg, sev = "info") => setSnack({ open: true, msg, sev });

  const validateField = (name, value) => {
    const next = { ...errors };

    switch (name) {
      case "name":
        if (!value?.trim()) next.name = "Please enter your company name";
        else delete next.name;
        break;
      case "email":
        if (!value?.trim()) next.email = "Company email is required";
        else if (!value.includes("@") || !value.includes(".")) next.email = "Please enter a valid email address";
        else delete next.email;
        break;
      case "password":
        if (!value) next.password = "Please create a password";
        else if (value.length < 8) next.password = "Password must be at least 8 characters long";
        else delete next.password;
        if (form.confirmPassword && value !== form.confirmPassword) next.confirmPassword = "Passwords do not match";
        else if (form.confirmPassword) delete next.confirmPassword;
        break;
      case "confirmPassword":
        if (value !== form.password) next.confirmPassword = "Passwords do not match";
        else delete next.confirmPassword;
        break;
      default:
        break;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFieldChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleSubmit = async () => {
    const fields = ["name", "email", "password", "confirmPassword"];
    let valid = true;
    fields.forEach((field) => {
      if (!validateField(field, form[field])) valid = false;
    });

    if (!valid) {
      openSnack("Please fix all errors before submitting", "error");
      return;
    }

    if (!form.gdpr_consent) {
      openSnack("Please accept the terms to complete your registration", "error");
      return;
    }

    setSubmitting(true);
    try {
      await register({
        full_name: form.name,
        email: form.email,
        password: form.password,
        company_name: form.name,
        website_url: null,
        referralCode: form.referralCode || null,
      });
      openSnack("Registration successful! Check your email to verify and get started.", "success");
      setTimeout(() => navigate(`${ROUTES.VERIFY_EMAIL}?email=${encodeURIComponent(form.email)}`, { replace: true }), 1800);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message;
      openSnack(msg || "Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      setForm((prev) => ({ ...prev, referralCode: ref }));
    }
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f1f5f9",
        py: { xs: 4, md: 6 },
        px: 2,
        backgroundImage:
          "radial-gradient(circle at 4% 0%, rgba(5,70,147,0.08) 0%, rgba(5,70,147,0) 40%), radial-gradient(circle at 100% 100%, rgba(5,150,105,0.06) 0%, rgba(5,150,105,0) 34%)",
      }}
    >
      <Container maxWidth={false} sx={{ width: "100%", maxWidth: 1240, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid #dbe6f3",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "360px 1fr" },
              minHeight: { lg: 760 },
            }}
          >
            <Box
              sx={{
                p: { xs: 2.6, sm: 3, lg: 3.2 },
                bgcolor: "#ecf4ff",
                borderRight: { lg: "1px solid #dbe6f3" },
                borderBottom: { xs: "1px solid #dbe6f3", lg: "none" },
              }}
            >
              <BusinessIcon sx={{ fontSize: 38, color: "#1976d2", mb: 1.6 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mb: 1.1, lineHeight: 1.25 }}>
                Build your Susha workspace
              </Typography>
              <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6, mb: 2 }}>
                Register once and start tracking assets, devices, warranty, AMC and lifecycle events from one secure portal.
              </Typography>

              <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: "#bfdbfe", bgcolor: "#fff" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e3a8a", mb: 0.6 }}>
                  Quick setup takes less than 3 minutes
                </Typography>
                <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.55 }}>
                  1. Company details
                  <br />
                  2. Secure admin login
                  <br />
                  3. Compliance consent
                </Typography>
              </Paper>

              <Box sx={{ mt: 2.2 }}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 0.35 }}>
                  HAVE AN ACCOUNT?
                </Typography>
                <Typography variant="body2" sx={{ color: "#334155", mt: 0.6 }}>
                  Use Sign In from the right panel after your company is verified.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: { xs: 2.4, sm: 3, md: 4 } }}>
              <Box sx={{ textAlign: { xs: "left", md: "center" }, mb: 3.2 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    mb: 1,
                    fontSize: { xs: "1.45rem", md: "2rem" },
                  }}
                >
                  Create Your Account
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680, mx: { md: "auto" }, lineHeight: 1.6 }}>
                  Set up your company workspace to start managing your assets.
                </Typography>
              </Box>

              <Box sx={{ width: "100%", maxWidth: FORM_MAX_WIDTH, mx: "auto" }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    We'll create a secure workspace for your organization. You can add your company logo and customize your profile after registration.
                  </Typography>
                </Box>

                <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2, borderColor: "#dbe6f3", bgcolor: "#f8fbff", mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.6 }}>
                    Faster setup with company SSO
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#475569", mb: 1.2 }}>
                    Recommended: create your workspace with Google Workspace or Microsoft 365 first.
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: `repeat(2, ${FIELD_WIDTH}px)` },
                      justifyContent: "center",
                      columnGap: 3,
                      rowGap: 1.2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Google />}
                      onClick={() => {
                        if (!form.gdpr_consent) {
                          openSnack("Please accept consent before continuing with Google signup", "warning");
                          return;
                        }
                        window.location.assign(AuthAPI.googleSignupStartUrl(ROUTES.WORKSPACE));
                      }}
                      sx={{ textTransform: "none", py: 1.2, fontWeight: 700 }}
                    >
                      Sign up with Google Workspace
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Microsoft />}
                      onClick={() => {
                        if (!form.gdpr_consent) {
                          openSnack("Please accept consent before continuing with Microsoft signup", "warning");
                          return;
                        }
                        window.location.assign(AuthAPI.m365SignupStartUrl(ROUTES.WORKSPACE));
                      }}
                      sx={{ textTransform: "none", py: 1.2, fontWeight: 700 }}
                    >
                      Sign up with Microsoft 365
                    </Button>
                  </Box>
                </Paper>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#334155" }}>
                    Company Information
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: `repeat(2, ${FIELD_WIDTH}px)` },
                      justifyContent: "center",
                      columnGap: 3,
                      rowGap: 2.5,
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", md: `${FIELD_WIDTH}px` } }}>
                      <TextField
                        label="Company or Organization Name"
                        fullWidth
                        required
                        value={form.name}
                        onChange={(e) => handleFieldChange("name", e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name || "The legal or registered name of your business"}
                        onBlur={() => validateField("name", form.name)}
                        InputLabelProps={{ shrink: true }}
                        placeholder="Enter your company name"
                        FormHelperTextProps={{ sx: { minHeight: "20px" } }}
                      />
                    </Box>

                    <Box sx={{ width: { xs: "100%", md: `${FIELD_WIDTH}px` } }}>
                      <TextField
                        label="Company Email Address"
                        type="email"
                        fullWidth
                        required
                        value={form.email}
                        onChange={(e) => handleFieldChange("email", e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email || "Use your official company email (not Gmail, Yahoo, etc.)"}
                        onBlur={() => validateField("email", form.email)}
                        InputLabelProps={{ shrink: true }}
                        placeholder="admin@yourcompany.com"
                        FormHelperTextProps={{ sx: { minHeight: "20px" } }}
                      />
                    </Box>

                    <Box sx={{ width: { xs: "100%", md: `${FIELD_WIDTH}px` } }}>
                      <TextField
                        label="Create Password"
                        type="password"
                        fullWidth
                        required
                        value={form.password}
                        onChange={(e) => handleFieldChange("password", e.target.value)}
                        error={!!errors.password}
                        helperText={errors.password || "At least 8 characters"}
                        onBlur={() => validateField("password", form.password)}
                        InputLabelProps={{ shrink: true }}
                        placeholder="••••••••"
                        FormHelperTextProps={{ sx: { minHeight: "20px" } }}
                      />
                    </Box>

                    <Box sx={{ width: { xs: "100%", md: `${FIELD_WIDTH}px` } }}>
                      <TextField
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        required
                        value={form.confirmPassword}
                        onChange={(e) => handleFieldChange("confirmPassword", e.target.value)}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword || "Re-enter your password"}
                        onBlur={() => validateField("confirmPassword", form.confirmPassword)}
                        InputLabelProps={{ shrink: true }}
                        placeholder="••••••••"
                        FormHelperTextProps={{ sx: { minHeight: "20px" } }}
                      />
                    </Box>

                    <Box sx={{ width: { xs: "100%", md: `${FIELD_WIDTH}px` } }}>
                      <TextField
                        label="Referred by / Partner Email (Optional)"
                        fullWidth
                        value={form.referralCode}
                        onChange={(e) => handleFieldChange("referralCode", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        placeholder="partner@company.com"
                        helperText="Enter your partner's email if you were referred"
                        FormHelperTextProps={{ sx: { minHeight: "20px" } }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Paper variant="outlined" sx={{ p: 2.5, bgcolor: "#f8fafc", borderColor: "#e2e8f0" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.gdpr_consent}
                        onChange={(e) => setForm({ ...form, gdpr_consent: e.target.checked })}
                        sx={{ alignSelf: "flex-start", mt: -0.5 }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        I confirm that I am authorized to register this company and agree to the{" "}
                        <MuiLink href="#" target="_blank" rel="noreferrer" sx={{ fontWeight: 600 }}>
                          Terms and Privacy Policy
                        </MuiLink>
                      </Typography>
                    }
                    sx={{ alignItems: "flex-start" }}
                  />
                </Paper>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  onClick={handleSubmit}
                  sx={{ mt: 3, py: 1.25, textTransform: "none", fontWeight: 700 }}
                >
                  {submitting ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Create Your Account"}
                </Button>

                <Box sx={{ mt: 2.6, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Have an account?{" "}
                    <MuiLink component="button" onClick={() => navigate(ROUTES.LOGIN, { replace: true })} sx={{ fontWeight: 600 }}>
                      Sign In
                    </MuiLink>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.sev} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
